import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MessagesSquare } from 'lucide-react';
import { OutreachContact, OutreachTeamMember, outreachTopicLabels } from '@radevents/shared';
import outreachService from '../../services/outreachService';
import { chatPreview, hasHistory } from './contactBits';

const INK = '#2C2C2C';
const MUTED = '#6A6A6A';

interface Props {
  organizerId: number;
  /** Für das Design-Lab: Daten von außen statt vom Server. */
  preset?: { contacts: OutreachContact[]; team: OutreachTeamMember[] };
}

function dateLabel(iso?: string) {
  return iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
}

/**
 * Auf der Veranstalterseite, nur für die Redaktion: hatten wir mit diesem Verein schon Kontakt,
 * wer kümmert sich, und was war zuletzt? Die Frage kommt genau hier auf, wenn man einen Verein
 * ansieht — ohne diesen Kasten hieß die Antwort „Outreach öffnen und suchen".
 */
export default function OrganizerOutreachStatus({ organizerId, preset }: Props) {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<OutreachContact[] | null>(preset?.contacts ?? null);
  const [team, setTeam] = useState<OutreachTeamMember[]>(preset?.team ?? []);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    if (preset) return;
    let alive = true;
    Promise.all([
      outreachService.contactsForOrganizer(organizerId),
      outreachService.team().catch(() => []),
    ])
      .then(([cs, tm]) => { if (alive) { setContacts(cs); setTeam(tm); } })
      .catch(() => { if (alive) setContacts([]); });
    return () => { alive = false; };
  }, [organizerId, preset]);

  if (contacts === null) return null;

  const withHistory = contacts.filter(hasHistory);
  const ownerName = (c: OutreachContact) => team.find(t => t.id === c.ownerUserId)?.name;

  const startChat = async () => {
    setOpening(true);
    try {
      const c = await outreachService.contactFromOrganizer(organizerId);
      navigate(`/admin/outreach?kontakt=${c.id}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? 'Kontakt nicht angelegt');
      setOpening(false);
    }
  };

  return (
    <section style={{ marginTop: 26, background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, padding: '18px 22px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 700, fontSize: 15, color: INK }}>
        <MessagesSquare size={17} aria-hidden />
        Kontakt mit dem Verein
      </div>

      {withHistory.length === 0 ? (
        <p style={{ fontSize: 14, color: MUTED, margin: '8px 0 12px', lineHeight: 1.55 }}>
          {contacts.length === 0
            ? 'Noch niemand hat diesen Veranstalter angeschrieben oder angerufen.'
            : 'Im Adressbuch, aber noch ohne Mail, Anruf oder Notiz.'}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {withHistory.map(c => (
            <li key={c.id} style={{ fontSize: 14, color: INK, lineHeight: 1.5 }}>
              <div>
                <strong>{c.name}</strong>
                {ownerName(c) ? <span style={{ color: MUTED }}> · zuständig: {ownerName(c)}</span> : <span style={{ color: MUTED }}> · niemand zuständig</span>}
                {(c.topics ?? []).length > 0 && (
                  <span style={{ color: MUTED }}> · {outreachTopicLabels(c.topics).join(', ')}</span>
                )}
              </div>
              <div style={{ color: MUTED, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {dateLabel(c.lastMessageAt)}: {chatPreview(c)}
              </div>
            </li>
          ))}
        </ul>
      )}

      {withHistory.length > 0 ? (
        <button className="vb-btn" onClick={() => navigate(`/admin/outreach?kontakt=${withHistory[0].id}`)}>
          Zum Verlauf
        </button>
      ) : (
        <button className="vb-btn" onClick={startChat} disabled={opening}>
          {opening ? 'Wird geöffnet…' : 'Anschreiben oder Anruf notieren'}
        </button>
      )}
    </section>
  );
}
